/**
 * Leo spotted as a queue of one card at a time, in pedagogical order.
 *
 * **What this replaces.** The inspector rendered every finding at once as a
 * stacked list. The PRD calls that out by name: a wall of stacked error text
 * produces banner blindness, and an author facing six simultaneous competing
 * actions resolves none of them. One card at a time, in a fixed order, is the
 * whole point.
 *
 * **The order is pedagogical, not by severity.** Overuse comes first because
 * if the question is going to be replaced wholesale, fixing its grammar was
 * wasted work. Content flaws come next because Bloom's and difficulty are
 * read off the question as written, so classifying before rewriting
 * classifies the wrong question. Metadata and category come last because
 * neither blocks anything.
 *
 * **Skip, and no dismiss.** The PRD offers Dismiss, which is persistent and
 * means "this is not a defect". Most authors deferring a finding have not
 * made that judgement, and a persistent dismiss quietly deletes work nobody
 * decided was unnecessary. Skip is the only defer: the card keeps its
 * finding, still counts as open, and stays reachable. Navigation is free in
 * both directions, so the queue orders the work without locking it.
 *
 * **No completion card.** An empty queue renders nothing. An author who
 * opens a clean question does not need to be congratulated, and a panel that
 * always shows something teaches people to stop reading it.
 *
 * **What is deliberately not in here.** Bloom's, difficulty and category do
 * not contribute to the AI Spotted tag. The PRD separates content integrity
 * from metadata completeness precisely so the badge keeps meaning something,
 * and `spottedTagForQueue` enforces that split. See `leoSpottedTag`.
 */

/** PRD defect dimensions, as a finding is grouped for the queue. */
export type LeoSpottedDimension =
  | "overuse"
  | "stem"
  | "distractors"
  | "pairing"
  | "reference"
  | "performance"
  | "rationale"
  | "metadata"
  | "category"

export type LeoSpottedCardKind =
  | "overuse"
  | "quality"
  | "missing-reference"
  | "performance"
  | "metadata"
  | "category"

/**
 * `skipped` is "not now" and still counts as open work. There is no
 * dismiss: telling an author their only way past a finding is to declare it
 * wrong is a false choice, and a persistent dismiss quietly deletes work
 * nobody decided was unnecessary. Skip defers; the card stays reachable.
 */
export type LeoSpottedCardState = "open" | "resolved" | "skipped"

export type LeoSpottedIssueInput = {
  id: string
  label: string
  /** Sheet cover id: stem, choices, format, passage, rationale, bloom… */
  cover: string
  /**
   * The CTA for this finding alone, e.g. "Replace All of the above".
   * `ComposeSpottedIssue` already carries one; a finding without one falls
   * back to the dimension's default verb.
   */
  outcome?: string
  /** What Leo is asked to do for this finding alone. */
  instruction?: string
  /**
   * True when the reading re-derives this finding from the question on
   * every read, so it clears when the text clears and not when the
   * author presses the button. Recording one of these as resolved hides
   * the card for the session and shows it again, open, afterwards.
   */
  selfClearing?: boolean
}

export type LeoSpottedQueueInput = {
  issues?: readonly LeoSpottedIssueInput[]
  /** Cohorts this question has been sat by. Over the threshold is overuse. */
  timesUsed?: number
  /** PRD: "used > 3 cohorts" signals the author should consider a variation. */
  exposureThreshold?: number
  /** Point biserial. `null` means never sat, which is not poor performance. */
  pbi?: number | null
  /** PRD: "PBI < 0.15 or Negative". */
  pbiFloor?: number
  bloom?: string
  difficulty?: string
  categoryIds?: readonly string[] | null
  categoryUnassigned?: boolean
  /**
   * The badge the row already carries. When it is set and nothing else
   * produced a card, the queue says the audit has not run rather than
   * leaving the author under a chip with an empty panel.
   */
  tag?: "needs-fixing" | "worth-a-look" | null
  /**
   * Findings on this question the author has already answered.
   *
   * A resolved finding leaves `issues`, so without this the queue rebuilds
   * empty and the panel vanishes the moment the last card is cleared. The
   * count is what separates a question that was always clean (say nothing)
   * from one the author just finished (say so).
   */
  resolvedCount?: number
  /**
   * Findings already answered. They stay in the stack as resolved cards
   * so the author can see what they have done and undo it — dropping
   * them made the stack shrink under them on every fix.
   */
  resolvedIssues?: readonly LeoSpottedIssueInput[]
  /**
   * True when the author has edited this question. With no findings
   * left it is the difference between "you fixed them" and "nobody has
   * looked at this yet", which decides whether the panel says All good
   * or says nothing at all.
   */
  edited?: boolean
  /** Persisted per question copy. These cards never re-enter the queue. */
  dismissedCardIds?: readonly string[]
}

export const LEO_SPOTTED_EXPOSURE_THRESHOLD = 3
export const LEO_SPOTTED_PBI_FLOOR = 0.15

/**
 * Cover to dimension.
 *
 * `difficulty` is the awkward one: it carries both "difficulty is unset"
 * (metadata) and "PBI is below the review floor" (performance), so the id is
 * what decides. Reading the label would work today and break the first time
 * someone rewords a finding.
 */
export function leoSpottedDimension(issue: LeoSpottedIssueInput): LeoSpottedDimension {
  // Performance findings are often filed under stem or difficulty. The card
  // stays general either way; the diagnosis waits until the author asks.
  if (isPerformanceIssue(issue)) return "performance"
  // Exposure findings are filed under `stem` in the case table because the
  // fix is a reword, but they are not stem defects: "Used 3 times, returning
  // students may recognise the wording" under the heading "Students have to
  // read the options to work out what is being asked" is two unrelated
  // sentences stacked. The label is the only thing that distinguishes them.
  if (EXPOSURE_LABEL.test(issue.label)) return "overuse"
  switch (issue.cover) {
    case "stem":
    case "format":
      return "stem"
    case "choices":
      return "distractors"
    case "alignment":
    case "duplicate":
      return "pairing"
    case "passage":
      return "reference"
    case "rationale":
      return "rationale"
    case "bloom":
    case "difficulty":
      return "metadata"
    case "category":
      return "category"
    default:
      return "stem"
  }
}

function isPerformanceIssueId(id: string): boolean {
  return /^performance[-.]/.test(id) || id === "performance"
}

/**
 * A performance finding, even when the case table files it under stem.
 *
 * "PBI 0.22, so the stem is not separating…" is a discrimination flag, not
 * a stem rewrite. The number and the guessed cause stay off the card.
 */
const PERFORMANCE_LABEL =
  /\bPBI\b|not separating|below the review floor|weak performance|discriminat/i

function isPerformanceIssue(issue: LeoSpottedIssueInput): boolean {
  return isPerformanceIssueId(issue.id) || PERFORMANCE_LABEL.test(issue.label)
}

const PERFORMANCE_HEADER = "This question may not be separating students"
const PERFORMANCE_BODY =
  "Strong and weak students answered this about the same way. Review a change before applying it."

/**
 * An exposure finding, whatever words it arrives in.
 *
 * "Used on 3 assessments already", "Used 3 times", "returning students may
 * recognise the wording". Matching on the label is unavoidable because the
 * case table files these under the `stem` cover, so rewording a finding
 * has to keep one of these shapes or it silently becomes a stem defect.
 */
const EXPOSURE_LABEL =
  /\bused\s+(?:on\s+)?\d+\s*(?:x\b|times?\b|assessments?\b|exams?\b)|returning students|recogni[sz]e the wording|heavy rotation/i

/**
 * Content dimensions in queue order, from the PRD.
 *
 * The PRD sets this twice and they agree. Issue 6 ("Missing reference +
 * stem / distractor flaws") spells out the remediation sequence as
 * **Exhibit synthesis -> Lead-in -> Choices**, and the Card 1 header
 * mapping lists Missing Reference above the writing flaws. So the exhibit
 * comes first: rewriting a stem that cites a chart nobody attached still
 * leaves a question a student cannot answer.
 *
 * Performance is last of the content dimensions because the PRD puts its
 * remediation out of scope — "automated AI diagnosis of the root causes of
 * low PBI ... will not be included", PBI serves only as a trigger. A card
 * whose whole offer is a diagnosis the system has not built should not
 * stand in front of defects Leo can actually repair.
 */
/** Writing flaws. One card for the whole group. Performance stays its own card. */
const QUALITY_GROUP: readonly LeoSpottedDimension[] = [
  "reference",
  "stem",
  "distractors",
  "pairing",
  "rationale",
]

/** The chip on the card, so the author knows what they are looking at. */
const DIMENSION_BADGE: Record<LeoSpottedDimension, string> = {
  overuse: "Exposure",
  stem: "Stem",
  distractors: "Answer choices",
  pairing: "Stem and choices",
  reference: "Reference",
  performance: "Performance",
  rationale: "Rationale",
  metadata: "Classification",
  category: "Category",
}

/** Default verb per dimension, when the finding does not carry its own. */
/**
 * The button on each card.
 *
 * Two or three words. These sit stacked and full-width in a narrow
 * rail, so a label that reads as a sentence wraps to two lines or
 * truncates. "with Leo" is dropped throughout because the sparkle on
 * the button already says it, but the noun is kept: "Rewrite" alone
 * does not say what, under a card about the stem with another about the
 * choices right behind it.
 */
const DIMENSION_CTA: Record<LeoSpottedDimension, string> = {
  overuse: "Replace question",
  stem: "Rewrite stem",
  distractors: "Fix choices",
  pairing: "Rewrite both",
  reference: "Add reference",
  performance: "Review the change",
  rationale: "Write rationale",
  metadata: "Apply",
  category: "Map category",
}

/**
 * Why the finding matters, per dimension.
 *
 * Not what Leo will do: the button already says that, and a card whose
 * headline is the defect and whose body restates the button has spent three
 * lines saying one thing. The body carries the part the author cannot work
 * out for themselves, which is what the defect costs them in a real sitting.
 */
const DIMENSION_BODY: Record<LeoSpottedDimension, string> = {
  overuse:
    "Students who sat an earlier exam may recognise this question, so it stops measuring what they know.",
  stem: "Students have to read the options to work out what is being asked.",
  distractors:
    "More than one option can be argued, so the question tests reading rather than knowledge.",
  pairing:
    "The choices assume things the stem never said, so students cannot tell what to take as given.",
  reference: "The stem points the student at something that is not attached.",
  performance: PERFORMANCE_BODY,
  rationale: "Nobody reviewing or reusing this question can see why the key is right.",
  metadata: "Without these the question is missing from end of term reporting.",
  category: "Unmapped questions do not appear in end of term reporting.",
}

export type LeoSpottedCard = {
  id: string
  kind: LeoSpottedCardKind
  dimension: LeoSpottedDimension
  /** Short chip: "Stem", "Answer choices", "Reference"… */
  badge: string
  /** The finding itself on an issue card; the card name otherwise. */
  header: string
  body: string
  /** Sparkle CTA. `null` on the completion card. */
  primaryLabel: string | null
  /**
   * What Leo is asked for when the CTA is clicked. Targeted at this one
   * finding, which is the difference from the old panel: its single
   * "Enhance with Leo" bundled every open issue into one ask and let Leo
   * decide what to change.
   */
  instruction?: string
  /** Findings this card is answering for. One, except on the group cards. */
  issues: LeoSpottedIssueInput[]
  /** True when every finding behind this card clears from the text alone. */
  selfClearing?: boolean
  /** Filled button. The usual CTA stays an outline Ask Leo button. */
  primarySolid?: boolean
  /** Primary action uses AskLeoButton (sparkle), not a plain Button. */
  primaryAskLeo?: boolean
  /** The card's two actions are the whole choice. Skip would be a third. */
  hideSkip?: boolean
  state: LeoSpottedCardState
}

export type LeoSpottedQueue = {
  /** Open cards, in pedagogical order. Skipped and resolved stay out. */
  cards: LeoSpottedCard[]
  activeIndex: number
}

const KIND_BY_DIMENSION: Partial<Record<LeoSpottedDimension, LeoSpottedCardKind>> = {
  overuse: "overuse",
  reference: "missing-reference",
  performance: "performance",
  metadata: "metadata",
  category: "category",
}

/**
 * What Leo is asked for, per dimension, when the finding does not carry
 * its own instruction.
 *
 * Each one names the single field to change and says to leave the rest
 * alone. Adding one here means adding it to
 * `isLeoQuestionEditDirective` too, or the draft pipeline reads the
 * instruction as the question's subject and writes a question about it. Without these the surfaces fell back to "rewrite this question
 * to resolve all open findings", which is how pressing Write rationale
 * produced a new stem and new choices and no rationale.
 */
const DIMENSION_INSTRUCTION: Record<LeoSpottedDimension, string> = {
  overuse:
    "Write a fresh variant of this question on the same objective, different enough that a student who sat the original would not recognise it. Keep the answer format.",
  stem:
    "Rewrite only the stem so it asks a complete question on its own, without the options. Leave the answer choices, the key and the rationale exactly as they are.",
  distractors:
    "Rewrite only the answer choices so each is plausible and only one is defensible. Keep the stem, the key's meaning and the rationale exactly as they are.",
  pairing:
    "Rewrite the stem and the answer choices together so the choices read as answers to the stem. Keep the learning objective, the answer format and the rationale.",
  reference:
    "Add the reference the stem points at, as text the student can read. Change nothing else.",
  performance:
    "Diagnose why this question failed to separate strong from weak students, and propose one change I can review before applying.",
  // Opens with "The rationale is missing" on purpose. leoAskedScope
  // reads that phrase as a rationale edit, so the reply writes a rationale
  // and leaves the stem, the choices, and the key alone. Reword it and the
  // ask is read as a new question, which is how "Write rationale" came
  // back as a new stem and new choices.
  rationale:
    "The rationale is missing. Write it: explain why the key is correct and why each distractor is wrong. Keep the stem, the answer choices and the key exactly as they are.",
  metadata:
    "Suggest the Bloom's level and difficulty for this question and explain why. Change nothing else.",
  category:
    "Suggest the exam category this question belongs to and explain why. Change nothing else.",
}

/** The button and the ask for a finding that does not carry its own. */
export function leoSpottedDefaultAsk(issue: LeoSpottedIssueInput): {
  outcome: string
  instruction: string
} {
  const dimension = leoSpottedDimension(issue)
  return {
    outcome: DIMENSION_CTA[dimension],
    instruction: DIMENSION_INSTRUCTION[dimension],
  }
}

/** One finding, one card. */
function issueCard(
  dimension: LeoSpottedDimension,
  issue: LeoSpottedIssueInput,
): LeoSpottedCard {
  const performance = dimension === "performance"
  return {
    id: issue.id,
    kind: KIND_BY_DIMENSION[dimension] ?? "quality",
    dimension,
    badge: DIMENSION_BADGE[dimension],
    // Performance stays general. The PBI and the guessed cause show up
    // only after the author asks Leo to diagnose.
    header: performance ? PERFORMANCE_HEADER : issue.label,
    body: performance ? PERFORMANCE_BODY : DIMENSION_BODY[dimension],
    primaryLabel: performance ? "Review the change" : (issue.outcome ?? DIMENSION_CTA[dimension]),
    instruction: issue.instruction ?? DIMENSION_INSTRUCTION[dimension],
    issues: [issue],
    selfClearing: issue.selfClearing,
    state: "open",
  }
}

/**
 * Stem and choices are one fix, even when only one of them is flagged.
 *
 * A choices card that says "leave the stem alone" is how Fix quality came
 * back as Fix choices: the first finding was cleared and the other one
 * opened its own card. One ask rewrites both, and names rationale or a
 * missing reference only when those findings are in the same group.
 */
function qualityInstruction(
  rows: readonly { dimension: LeoSpottedDimension; issue: LeoSpottedIssueInput }[],
): string {
  const dimensions = new Set(rows.map((row) => row.dimension))
  const labels = rows.map((row) => row.issue.label)
  const parts: string[] = []
  if (
    dimensions.has("stem") ||
    dimensions.has("distractors") ||
    dimensions.has("pairing")
  ) {
    parts.push(
      "Rewrite the stem and the answer choices together so the stem states every fact a choice depends on and only one choice is defensible.",
    )
  }
  if (dimensions.has("reference")) {
    parts.push("Add the reference the stem points at, as text the student can read.")
  }
  if (dimensions.has("rationale")) {
    parts.push(
      "The rationale is missing. Write it: explain why the key is correct and why each distractor is wrong.",
    )
  }
  parts.push(
    `Findings: ${labels.join(". ")}. Keep the learning objective and the question type.`,
  )
  return parts.join(" ")
}

/** Writing findings share one improvement headline, not a count. */
export const QUALITY_IMPROVEMENT_HEADER = "Question quality can be improved"

/**
 * One grouped card: a plain-language headline and the specific findings
 * underneath, without "N findings to fix" style titles.
 */
export function qualityGroupCardCopy(
  rows: readonly { dimension: LeoSpottedDimension; issue: LeoSpottedIssueInput }[],
): { header: string; body: string } {
  const issues = rows.map((row) => row.issue)
  if (issues.length === 0) {
    return { header: QUALITY_IMPROVEMENT_HEADER, body: "" }
  }
  if (issues.length === 1) {
    const issue = issues[0]
    return {
      header: QUALITY_IMPROVEMENT_HEADER,
      body: `${issue.label}. ${DIMENSION_BODY[leoSpottedDimension(issue)]}`,
    }
  }
  return {
    header: QUALITY_IMPROVEMENT_HEADER,
    body: issues.map((row) => row.label).join(". "),
  }
}

/** Collapsed stack copy when every card was skipped. No counts. */
export function leoSpottedDeferredCollapseTitle(queue: LeoSpottedQueue): string {
  const skipped = queue.cards.filter(
    (row) =>
      row.state === "skipped" &&
      row.id !== "all-good" &&
      row.id !== "not-checked" &&
      row.id !== "unaudited",
  )
  if (skipped.length === 0) return "Review when you are ready"
  const kinds = new Set(skipped.map((row) => row.kind))
  if (kinds.size === 1) {
    const kind = skipped[0]?.kind
    if (kind === "quality") return QUALITY_IMPROVEMENT_HEADER
    if (kind === "metadata") return "Classification can be improved"
    if (kind === "category") return "Category can be improved"
    if (kind === "performance") return PERFORMANCE_HEADER
    if (kind === "overuse") return skipped[0]?.header ?? "Exposure can be reduced"
  }
  return "Review when you are ready"
}

/**
 * Stem, choices, rationale, reference, and pairing are one group.
 *
 * A card per finding made the author page through one problem at a time
 * when they are all the same kind of work. One card lists them and asks
 * Leo to fix that group. Overuse, performance, classification, and
 * category stay their own cards.
 */
function qualityGroupCard(
  rows: readonly { dimension: LeoSpottedDimension; issue: LeoSpottedIssueInput }[],
): LeoSpottedCard {
  const issues = rows.map((row) => row.issue)
  const copy = qualityGroupCardCopy(rows)
  return {
    id: "quality",
    kind: "quality",
    dimension: "pairing",
    badge: "Writing",
    header: copy.header,
    body: copy.body,
    primaryLabel: "Fix quality",
    instruction: qualityInstruction(rows),
    issues,
    selfClearing: issues.every((issue) => issue.selfClearing),
    state: "open",
  }
}

/** A card that stands for a gap rather than for a finding. */
function gapCard(
  id: string,
  dimension: LeoSpottedDimension,
  header: string,
  issues: LeoSpottedIssueInput[] = [],
  /** Names what this card does. Falls back to the dimension's verb. */
  action?: string,
  body?: string,
): LeoSpottedCard {
  return {
    id,
    kind: KIND_BY_DIMENSION[dimension] ?? "quality",
    dimension,
    badge: DIMENSION_BADGE[dimension],
    header,
    body: body ?? DIMENSION_BODY[dimension],
    primaryLabel: action ?? DIMENSION_CTA[dimension],
    instruction: DIMENSION_INSTRUCTION[dimension],
    issues,
    state: "open",
  }
}

/**
 * A tag with nothing behind it.
 *
 * The bank badges a question Worth a look or Needs fixing, the author opens
 * it, and the panel has no findings to show because the rules tables only
 * cover a couple of dozen ids. A badge that leads nowhere is worse than no
 * badge, so the queue says plainly that the audit has not run and offers to
 * run it, rather than rendering an empty panel under a red chip.
 */
function settledCard(issues: readonly LeoSpottedIssueInput[]): LeoSpottedCard {
  return {
    id: "all-good",
    kind: "quality",
    dimension: "stem",
    badge: "All good",
    header: "All good",
    body: issues.length
      ? issues.map((issue) => issue.label).join(". ")
      : "You have answered everything Leo raised on this question.",
    primaryLabel: null,
    issues: [...issues],
    state: "resolved",
  }
}

/** Findings the settled card should name, without a skipped card. */
function settledIssuesFrom(cards: readonly LeoSpottedCard[]): LeoSpottedIssueInput[] {
  const seen = new Set<string>()
  const issues: LeoSpottedIssueInput[] = []
  for (const card of cards) {
    if (card.state === "skipped" || card.id === "all-good") continue
    const rows = card.issues.length
      ? card.issues
      : [{ id: card.id, label: card.header, cover: card.dimension }]
    for (const issue of rows) {
      if (seen.has(issue.id)) continue
      seen.add(issue.id)
      issues.push(issue)
    }
  }
  return issues
}

/**
 * The bank says Not checked. The sheet offers a read or a diagnosis.
 * The queue builder still returns nothing for an unbadged row, so the
 * bank cell and the composer stay quiet until there is a finding.
 */
export type NotCheckedDiagnosisPhase =
  | "idle"
  | "diagnosing"
  | "separating"
  | "unsat"

export function notCheckedLeoSpottedCard(
  phase: NotCheckedDiagnosisPhase = "idle",
  separationReady = true,
): LeoSpottedCard {
  if (phase === "separating") {
    return {
      id: "not-checked",
      kind: "quality",
      dimension: "performance",
      badge: "All good",
      header: "Students separate on this question",
      body: "Strong and weak students did not answer this the same way. There is no separation problem to review.",
      primaryLabel: null,
      issues: [
        {
          id: "diagnosis-separating",
          label: "Students separate on this question",
          cover: "performance",
        },
      ],
      state: "resolved",
      hideSkip: true,
    }
  }
  if (phase === "unsat") {
    return {
      id: "not-checked",
      kind: "quality",
      dimension: "performance",
      badge: "Not checked",
      header: "No student responses yet",
      body: "Separation needs results from at least one administration. Review writing quality in the sheet until students have sat this question.",
      primaryLabel: null,
      issues: [],
      state: "open",
      hideSkip: true,
    }
  }
  if (phase === "diagnosing") {
    return {
      id: "not-checked",
      kind: "quality",
      dimension: "performance",
      badge: "Not checked",
      header: "Diagnosing this question",
      body: "Checking whether strong and weak students answer this differently.",
      primaryLabel: null,
      primaryAskLeo: false,
      issues: [],
      state: "open",
      hideSkip: true,
    }
  }
  if (!separationReady) {
    return notCheckedLeoSpottedCard("unsat")
  }
  return {
    id: "not-checked",
    kind: "quality",
    dimension: "stem",
    badge: "Not checked",
    header: "This question has not been checked",
    body: "Diagnose whether students separate on this question.",
    primaryLabel: "Diagnose",
    primaryAskLeo: true,
    hideSkip: true,
    issues: [],
    state: "open",
  }
}

function unauditedCard(tag: "needs-fixing" | "worth-a-look"): LeoSpottedCard {
  return {
    id: "unaudited",
    kind: "quality",
    dimension: "stem",
    badge: "Not checked yet",
    header:
      tag === "worth-a-look"
        ? "This question is flagged, but Leo has not read it yet"
        : "This question is flagged, but Leo has not read it yet",
    body: "Run the quality check to see what Leo finds in the stem, the answer choices, and the rationale.",
    primaryLabel: "Check quality",
    issues: [],
    state: "open",
  }
}

/**
 * Build the queue.
 *
 * Smart skipping is structural: a card with nothing to say is never built,
 * so the author lands on the first real issue rather than paging through
 * three empty steps to reach it.
 */
export function buildLeoSpottedQueue(
  input: LeoSpottedQueueInput,
): LeoSpottedQueue {
  const dismissed = new Set(input.dismissedCardIds ?? [])
  const issues = input.issues ?? []
  const byDimension = new Map<LeoSpottedDimension, LeoSpottedIssueInput[]>()
  for (const issue of issues) {
    const dimension = leoSpottedDimension(issue)
    const bucket = byDimension.get(dimension)
    if (bucket) bucket.push(issue)
    else byDimension.set(dimension, [issue])
  }

  const cards: LeoSpottedCard[] = []

  // Priority card. First, because a replaced question makes every other
  // repair on this one moot.
  const threshold = input.exposureThreshold ?? LEO_SPOTTED_EXPOSURE_THRESHOLD
  const exposureIssues = byDimension.get("overuse") ?? []
  if (exposureIssues.length > 0) {
    // An authored exposure finding says it better than a count would.
    cards.push(issueCard("overuse", exposureIssues[0]))
  } else if ((input.timesUsed ?? 0) > threshold) {
    cards.push(
      gapCard(
        "overuse",
        "overuse",
        input.timesUsed
          ? `Used in ${input.timesUsed} assessments`
          : "Used across several assessments",
      ),
    )
  }

  // Stem and choices are never their own cards. One quality card asks for
  // both, even when only one of them is flagged. A reference or rationale
  // with nothing else wrong keeps its own verb.
  const qualityRows = QUALITY_GROUP.flatMap((dimension) =>
    (byDimension.get(dimension) ?? []).map((issue) => ({ dimension, issue })),
  )
  // Blueprint alignment is about the assessment, so it stays its own card
  // even when the question also has writing findings.
  const writingRows = qualityRows.filter(
    (row) => row.issue.id !== "assessment-blueprint-alignment",
  )
  const alignmentRows = qualityRows.filter(
    (row) => row.issue.id === "assessment-blueprint-alignment",
  )
  if (writingRows.length > 0) {
    cards.push(qualityGroupCard(writingRows))
  }
  for (const row of alignmentRows) {
    cards.push(issueCard(row.dimension, row.issue))
  }

  for (const issue of byDimension.get("performance") ?? []) {
    cards.push(issueCard("performance", issue))
  }

  // Metadata, which never blocks anything and so never leads.
  const metadataIssues = byDimension.get("metadata") ?? []
  const bloomMissing = !input.bloom?.trim()
  const difficultyMissing = !input.difficulty?.trim()
  if (metadataIssues.length > 0 || bloomMissing || difficultyMissing) {
    const missing = [
      bloomMissing ? "Bloom's level" : "",
      difficultyMissing ? "difficulty" : "",
    ].filter(Boolean)
    cards.push(
      gapCard(
        "metadata",
        "metadata",
        "Classification",
        metadataIssues,
        missing.length === 1 ? `Set ${missing[0]}` : "Set both",
        missing.length > 0
          ? `${missing.join(" and ")} not set`
          : "Review Bloom's level and difficulty",
      ),
    )
  }

  // Category. Design is still open, so this states the gap and hands off
  // rather than promising a resolution flow that does not exist.
  const categoryIssues = byDimension.get("category") ?? []
  const categoryMissing =
    input.categoryUnassigned === true ||
    (input.categoryIds != null && !input.categoryIds.some((id) => id.trim()))
  if (categoryIssues.length > 0 || categoryMissing) {
    cards.push(gapCard("category", "category", "Category is not mapped", categoryIssues))
  }

  const open = cards.filter((row) => !dismissed.has(row.id))
  const answered = (input.resolvedIssues ?? []).filter(
    (issue) => !dismissed.has(`issue:${issue.id}`),
  )

  if (open.length === 0) {
    if ((input.resolvedCount ?? 0) > 0 || input.edited || answered.length > 0) {
      return { cards: [settledCard(answered)], activeIndex: 0 }
    }
    if (input.tag) return { cards: [unauditedCard(input.tag)], activeIndex: 0 }
    return { cards: [], activeIndex: 0 }
  }

  return { cards: open, activeIndex: 0 }
}

// ─── State ──────────────────────────────────────────────────────────────────

function setState(
  queue: LeoSpottedQueue,
  cardId: string,
  state: LeoSpottedCardState,
): LeoSpottedQueue {
  return {
    ...queue,
    cards: queue.cards.map((row) =>
      row.id === cardId ? { ...row, state } : row,
    ),
  }
}

/**
 * The author acted on the card. Stay on it so it can turn into All good
 * in place. The next open card is one page away.
 */
export function resolveLeoSpottedCard(
  queue: LeoSpottedQueue,
  cardId: string,
): LeoSpottedQueue {
  const next = setState(queue, cardId, "resolved")
  // The last open card becomes the one summary. Earlier cards stay so a
  // fix can turn into All good while something else is still open.
  if (openLeoSpottedCards(next).length > 0) return next
  return { cards: [settledCard(settledIssuesFrom(next.cards))], activeIndex: 0 }
}

/**
 * After the check-card animation, tuck the resolved card behind the stack
 * and bring the next open card forward.
 */
export function finishResolvedLeoSpottedCard(
  queue: LeoSpottedQueue,
  cardId: string,
): LeoSpottedQueue {
  if (openLeoSpottedCards(queue).length === 0) {
    return { cards: [settledCard(settledIssuesFrom(queue.cards))], activeIndex: 0 }
  }
  const from = queue.cards.findIndex((row) => row.id === cardId)
  if (from < 0) return queue
  const cards = [...queue.cards]
  const [resolved] = cards.splice(from, 1)
  cards.push(resolved)
  const nextOpen = cards.findIndex(
    (row) => row.state === "open" || row.state === "skipped",
  )
  return {
    ...queue,
    cards,
    activeIndex: nextOpen >= 0 ? nextOpen : 0,
  }
}

/**
 * "Not now." The card keeps its finding and still counts as open work, and
 * the author can page back to it. It is the only defer, because the
 * alternative the PRD offers makes the author declare the finding wrong in
 * order to move on, and that is not what deferring means.
 */
export function skipLeoSpottedCard(
  queue: LeoSpottedQueue,
  cardId: string,
): LeoSpottedQueue {
  return advance(setState(queue, cardId, "skipped"), cardId)
}

/** Undo a skip. Paging back to a card does not by itself reopen it. */
export function reopenLeoSpottedCard(
  queue: LeoSpottedQueue,
  cardId: string,
): LeoSpottedQueue {
  return setState(queue, cardId, "open")
}

/** After acting, land on the next card still wanting an answer. */
function advance(queue: LeoSpottedQueue, fromId: string): LeoSpottedQueue {
  const from = queue.cards.findIndex((row) => row.id === fromId)
  const next = queue.cards.findIndex(
    (row, index) => index > from && row.state === "open",
  )
  return {
    ...queue,
    activeIndex: next >= 0 ? next : queue.cards.length - 1,
  }
}

/**
 * Free navigation, clamped to the queue.
 *
 * Every card stays reachable whatever its state, because the point of the
 * queue is sequence, not a lock. An author who skipped Card 1 to look at
 * Bloom's has to be able to get back to it.
 */
export function goToLeoSpottedCard(
  queue: LeoSpottedQueue,
  index: number,
): LeoSpottedQueue {
  const clamped = Math.min(Math.max(index, 0), queue.cards.length - 1)
  return { ...queue, activeIndex: clamped }
}

export function activeLeoSpottedCard(
  queue: LeoSpottedQueue,
): LeoSpottedCard | undefined {
  return queue.cards[queue.activeIndex]
}

/** Cards still wanting an answer. A skipped card is one of them. */
export function openLeoSpottedCards(
  queue: LeoSpottedQueue,
): LeoSpottedCard[] {
  return queue.cards.filter(
    (row) => row.state === "open" || row.state === "skipped",
  )
}

/** Every finding card was deferred. Summary cards do not count. */
export function leoSpottedAllCardsSkipped(queue: LeoSpottedQueue): boolean {
  const cards = queue.cards.filter(
    (row) => row.id !== "all-good" && row.id !== "not-checked" && row.id !== "unaudited",
  )
  if (cards.length === 0) return false
  return cards.every((row) => row.state === "skipped")
}

export function leoSpottedSkippedCardCount(queue: LeoSpottedQueue): number {
  return queue.cards.filter((row) => row.state === "skipped").length
}

export function leoSpottedQueueSettled(queue: LeoSpottedQueue): boolean {
  return openLeoSpottedCards(queue).length === 0
}

/** Index of a card, for navigating back to one the author deferred. */
export function leoSpottedCardIndex(
  queue: LeoSpottedQueue,
  cardId: string,
): number {
  return queue.cards.findIndex((row) => row.id === cardId)
}

// ─── The badge ──────────────────────────────────────────────────────────────

/**
 * The AI Spotted tag, per the PRD's column separation rule.
 *
 * Content integrity only. Bloom's, difficulty and category are metadata
 * completeness and get their own columns, so they never colour this badge —
 * which is a behaviour change: `openQuestionSpotted` currently forces
 * `needs-fixing` on a question whose only fault is an unassigned category,
 * and the PRD says that is exactly the alert fatigue to avoid. Missing
 * rationale is likewise suppressed here and surfaced only in the editor.
 *
 * `null` is the muted dash: clean, or never audited. It is not "good".
 */
export function leoSpottedTag(
  input: LeoSpottedQueueInput,
): "needs-fixing" | "worth-a-look" | null {
  const issues = input.issues ?? []

  const hasDefect = issues.some((issue) => {
      const dimension = leoSpottedDimension(issue)
      return (
        dimension === "stem" ||
        dimension === "distractors" ||
        dimension === "pairing" ||
        dimension === "reference" ||
        dimension === "performance"
      )
    })

  const floor = input.pbiFloor ?? LEO_SPOTTED_PBI_FLOOR
  // `null` is never sat, which is not the same statement as scoring badly.
  const weakPbi = input.pbi != null && input.pbi < floor

  if (hasDefect || weakPbi) return "needs-fixing"

  const threshold = input.exposureThreshold ?? LEO_SPOTTED_EXPOSURE_THRESHOLD
  const overexposed =
    (input.timesUsed ?? 0) > threshold ||
    issues.some((issue) => leoSpottedDimension(issue) === "overuse")
  if (overexposed) return "worth-a-look"

  return null
}
